<script setup lang="ts">
import { inject, onBeforeUnmount, onMounted } from "vue";
import type { ToastInterface } from "@components/toast/index";

interface IdleLogoutConfig {
    timeoutMinutes: number;
    warningSeconds?: number;
    loginUrl: string;
    warningMessage?: string;
}

const toast = inject<Partial<ToastInterface>>("toast");
const axiosInstance = inject<
    { post: (url: string) => Promise<unknown> } | undefined
>("axios");
const config = (window as any)?.appConfig?.IDLE_LOGOUT as
    | IdleLogoutConfig
    | undefined;

let warningTimer: number | undefined;
let logoutTimer: number | undefined;
let loggingOut = false;
let initialized = false;

const activityEvents = [
    "mousemove",
    "mousedown",
    "keydown",
    "touchstart",
    "scroll",
    "wheel",
];

const clearTimers = () => {
    if (warningTimer) {
        window.clearTimeout(warningTimer);
    }
    if (logoutTimer) {
        window.clearTimeout(logoutTimer);
    }
};

const showWarning = (
    warningSeconds: number,
    warningMs: number,
    warningMessage?: string,
) => {
    if (warningMs <= 0) {
        return;
    }

    if (toast?.warning) {
        const minutes = Math.max(1, Math.ceil(warningSeconds / 60));
        const message =
            warningMessage ??
            `You will be logged out in ${minutes} minute${minutes === 1 ? "" : "s"} due to inactivity.`;

        toast.warning(message, {
            timeout: Math.min(10000, warningMs),
        });
    }
};

const triggerLogout = (loginUrl: string) => {

    // console.log("Logging out due to inactivity");
    if (loggingOut) {
        return;
    }

    loggingOut = true;

    window.location.href = loginUrl;
};

const resetTimers = (
    timeoutMs: number,
    warningMs: number,
    warningSeconds: number,
    warningMessage?: string,
) => {
    if (loggingOut) {
        return;
    }

    clearTimers();

    if (warningMs > 0 && warningMs < timeoutMs) {
        warningTimer = window.setTimeout(() => {
            showWarning(warningSeconds, warningMs, warningMessage);
        }, timeoutMs - warningMs);
    }

    logoutTimer = window.setTimeout(() => {
        if (!config) {
            return;
        }
        triggerLogout(config.loginUrl);
    }, timeoutMs);
};

const onActivity = () => {
    if (!config) {
        return;
    }

    const timeoutMs = config.timeoutMinutes * 60 * 1000;
    const warningSeconds = config.warningSeconds ?? 0;
    const warningMs = warningSeconds > 0 ? warningSeconds * 1000 : 0;

    resetTimers(timeoutMs, warningMs, warningSeconds, config.warningMessage);
};

const onVisibilityChange = () => {
    if (!document.hidden) {
        onActivity();
    }
};

onMounted(() => {
    if (!config || !config.timeoutMinutes || config.timeoutMinutes <= 0) {
        return;
    }

    initialized = true;

    const timeoutMs = config.timeoutMinutes * 60 * 1000;
    const warningSeconds = config.warningSeconds ?? 0;
    const warningMs = warningSeconds > 0 ? warningSeconds * 1000 : 0;

    const secondsUntilLogout = Math.floor(timeoutMs / 1000);
    const secondsUntilWarning =
        warningMs > 0 && warningMs < timeoutMs
            ? Math.floor((timeoutMs - warningMs) / 1000)
            : 0;

    // console.log(`Idle logout in ${secondsUntilLogout} seconds`);

    if (secondsUntilWarning > 0) {
        // console.log(`Idle warning in ${secondsUntilWarning} seconds`);
    }

    activityEvents.forEach((eventName) => {
        window.addEventListener(eventName, onActivity, { passive: true });
    });

    document.addEventListener("visibilitychange", onVisibilityChange);

    onActivity();
});


onBeforeUnmount(() => {
    if (!initialized) {
        return;
    }

    activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity);
    });

    document.removeEventListener("visibilitychange", onVisibilityChange);
    clearTimers();
});
</script>

<template>
    <div aria-hidden="true" class="d-none"></div>
</template>
